/** ************************************************************************
 * TEMPLATE NAME: DEFAULT EXAMPLE
 * PURPOSE: To show one example of the bPerx iFrame on our
 *          website, alongside the integration documents
 *
 * A "template" is a specific combination of Atomic UI components.
 * The idea is to compose these atomic components together, creating
 * a specific template that seems common enough in the real world.
 *
 * This particular template is the DEFAULT/EXAMPLE template, shown on
 * our website at https://embed-docs.bperx.com/example
 *
 * This is the "DEFAULT" template, it has
 * -- Selection made on the left side
 * -- The button shows a horizontal slider/carousel of offered items
 *
 * @file   Template, default with carousel
 * @author Paul J Stales, Steve Yardumian
 * @since  2.0.0
 ************************************************************************ */

import { h } from 'preact'
import { TemplateProps } from '../TemplateProps'

import OfferChangeSelectionButton from '../../components/offerChangeSelectionComponents/offerChangeSelectionButton'
import OfferOptCheckbox from '../../components/offerOptControlComponents/offerOptCheckbox'
import Products from '../../components/offerDisplayProductComponents/products'
import {
  OPT_CHECKBOX_STRINGS,
  OFFER_NO_CHARGE_STRINGS
} from '../../../../../common/constants/ConstantsValues'
import OfferGiftText from '../../components/contentStaticComponents/offerGiftText'
import OfferTermsText from '../../components/contentStaticComponents/offerTermsText'
import OfferNoChargeText from '../../components/contentStaticComponents/offerNoChargeText'
import ChosenMagazineText from '../../components/contentDynamicComponents/chosenMagazineText'
import ChosenMagazineImage from '../../components/contentDynamicComponents/chosenMagazineImage'
import { css } from 'aphrodite'
import { IFRAME_CLASSNAMES } from '../../../../../common/constants/ConstantsIFrameClassNames'

const DefaultTemplate = ({
  styles,
  isIntentToOptIn,
  transactionInformation,
  setTransactionInformation,
  bperxProducts,
  areProductsVisible,
  setProductsVisible,
  areTermsVisible,
  setTermsVisible,
  setDoesPartnerNeedUpdateForTransactionInformation,
  partnerSuppliedInfo,
  displayEvent,
  setDisplayEvent,
  displayEventHandler
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
TemplateProps): any => {
  return (
    <div
      class={`${IFRAME_CLASSNAMES.__________BPERX_IFRAME__________}${' '}${
        styles[IFRAME_CLASSNAMES.__________BPERX_IFRAME__________] === undefined
          ? ''
          : css(styles[IFRAME_CLASSNAMES.__________BPERX_IFRAME__________])
      }`}
    >
      {/*
       * ========================================================================
       * The selected offer, shown when products are hidden
       * ========================================================================
       */}
      {!areProductsVisible && (
        <div>
          <div
            class={`${IFRAME_CLASSNAMES.BPERX_SELECTED_OFFER_CONTAINER}${' '}${
              styles[IFRAME_CLASSNAMES.BPERX_SELECTED_OFFER_CONTAINER] ===
              undefined
                ? ''
                : css(styles[IFRAME_CLASSNAMES.BPERX_SELECTED_OFFER_CONTAINER])
            }`}
          >
            <div
              class={`${
                IFRAME_CLASSNAMES.BPERX_MAGAZINE_IMAGE_SUBCONTAINER
              }${' '}${
                styles[IFRAME_CLASSNAMES.BPERX_MAGAZINE_IMAGE_SUBCONTAINER] ===
                undefined
                  ? ''
                  : css(
                      styles[
                        IFRAME_CLASSNAMES.BPERX_MAGAZINE_IMAGE_SUBCONTAINER
                      ]
                    )
              }`}
            >
              <ChosenMagazineImage
                styles={styles}
                coverImageURL={
                  transactionInformation.customerChoice.productMagazineChosen
                    .coverImageURL
                }
                description={
                  transactionInformation.customerChoice.productMagazineChosen
                    .description
                }
              />
            </div>

            <div
              class={`${
                IFRAME_CLASSNAMES.BPERX_OFFER_INFORMATION_SUBCONTAINER
              }${' '}${
                styles[
                  IFRAME_CLASSNAMES.BPERX_OFFER_INFORMATION_SUBCONTAINER
                ] === undefined
                  ? ''
                  : css(
                      styles[
                        IFRAME_CLASSNAMES.BPERX_OFFER_INFORMATION_SUBCONTAINER
                      ]
                    )
              }`}
            >
              <OfferGiftText styles={styles} giftText={'Our Gift to You'} />

              <ChosenMagazineText
                styles={styles}
                numberOfIssues={
                  transactionInformation.customerChoice.productMagazineChosen
                    .isSubscription
                    ? transactionInformation.customerChoice
                        .productMagazineChosen.issues
                    : undefined
                }
                wordChoiceForIssue={
                  transactionInformation.customerChoice.productMagazineChosen
                    .isSubscription
                    ? 'issues of '
                    : ''
                }
                magazineTitle={
                  transactionInformation.customerChoice.productMagazineChosen
                    .title
                }
                isOptingIn={transactionInformation.customerChoice.isOptingIn}
                optedOutText={
                  partnerSuppliedInfo.partnerCustomizations?.optedOutText
                }
              />

              <OfferNoChargeText
                styles={styles}
                noChargeText={
                  partnerSuppliedInfo.partnerCustomizations?.noChargeText
                    ? partnerSuppliedInfo.partnerCustomizations.noChargeText
                    : transactionInformation.customerChoice
                        .productMagazineChosen.isSubscription === undefined
                    ? OFFER_NO_CHARGE_STRINGS.MAGAZINE_CHOSEN_IS_SUB_TEXT
                    : transactionInformation.customerChoice
                        .productMagazineChosen.isSubscription
                    ? OFFER_NO_CHARGE_STRINGS.MAGAZINE_CHOSEN_IS_SUB_TEXT
                    : OFFER_NO_CHARGE_STRINGS.DEFAULT_NO_CHARGE_TEXT
                }
                areTermsVisible={areTermsVisible}
                setTermsVisible={setTermsVisible}
                partnerSuppliedInfo={partnerSuppliedInfo}
                transactionInformation={transactionInformation}
                setTransactionInformation={setTransactionInformation}
                setDoesPartnerNeedUpdateForTransactionInformation={
                  setDoesPartnerNeedUpdateForTransactionInformation
                }
              />

              <div
                class={`${
                  IFRAME_CLASSNAMES.BPERX_USER_INTERACTION_CONTAINER
                }${' '}${
                  styles[IFRAME_CLASSNAMES.BPERX_USER_INTERACTION_CONTAINER] ===
                  undefined
                    ? ''
                    : css(
                        styles[
                          IFRAME_CLASSNAMES.BPERX_USER_INTERACTION_CONTAINER
                        ]
                      )
                }`}
              >
                <OfferChangeSelectionButton
                  styles={styles}
                  numberOfProductsInOffer={bperxProducts.length}
                  areProductsVisible={areProductsVisible}
                  setProductsVisible={setProductsVisible}
                  transactionInformation={transactionInformation}
                  setTransactionInformation={setTransactionInformation}
                  setDoesPartnerNeedUpdateForTransactionInformation={
                    setDoesPartnerNeedUpdateForTransactionInformation
                  }
                  displayEvent={displayEvent}
                  setDisplayEvent={setDisplayEvent}
                  displayEventHandler={displayEventHandler}
                />

                <OfferOptCheckbox
                  styles={styles}
                  isOptElementAlwaysVisible={
                    partnerSuppliedInfo.partnerCustomizations
                      ?.isOptElementAlwaysVisible
                  }
                  areProductsVisible={areProductsVisible}
                  isIntentToOptIn={isIntentToOptIn}
                  optTextString={
                    isIntentToOptIn
                      ? OPT_CHECKBOX_STRINGS.OPT_IN
                      : OPT_CHECKBOX_STRINGS.OPT_OUT
                  }
                  transactionInformation={transactionInformation}
                  setTransactionInformation={setTransactionInformation}
                  setDoesPartnerNeedUpdateForTransactionInformation={
                    setDoesPartnerNeedUpdateForTransactionInformation
                  }
                  displayEvent={displayEvent}
                  setDisplayEvent={setDisplayEvent}
                  displayEventHandler={displayEventHandler}
                />
              </div>
            </div>
          </div>
          {/* The terms shows in the information window. */}
          {areTermsVisible && (
            <OfferTermsText
              styles={styles}
              areTermsVisible={areTermsVisible}
              transactionInformation={transactionInformation}
            />
          )}
        </div>
      )}

      {/*
       * ========================================================================
       * The selected offer, hides the selected offer
       * ========================================================================
       */}
      {areProductsVisible && (
        <div
          class={`${IFRAME_CLASSNAMES.BPERX_PRODUCTS_CONTAINER}${' '}${
            styles[IFRAME_CLASSNAMES.BPERX_PRODUCTS_CONTAINER] === undefined
              ? ''
              : css(styles[IFRAME_CLASSNAMES.BPERX_PRODUCTS_CONTAINER])
          }`}
        >
          <OfferChangeSelectionButton
            styles={styles}
            numberOfProductsInOffer={bperxProducts.length}
            areProductsVisible={areProductsVisible}
            setProductsVisible={setProductsVisible}
            transactionInformation={transactionInformation}
            setTransactionInformation={setTransactionInformation}
            setDoesPartnerNeedUpdateForTransactionInformation={
              setDoesPartnerNeedUpdateForTransactionInformation
            }
            displayEvent={displayEvent}
            setDisplayEvent={setDisplayEvent}
            displayEventHandler={displayEventHandler}
          />

          <OfferOptCheckbox
            styles={styles}
            isOptElementAlwaysVisible={
              partnerSuppliedInfo.partnerCustomizations
                ?.isOptElementAlwaysVisible
            }
            areProductsVisible={areProductsVisible}
            isIntentToOptIn={isIntentToOptIn}
            optTextString={
              isIntentToOptIn
                ? OPT_CHECKBOX_STRINGS.OPT_IN
                : OPT_CHECKBOX_STRINGS.OPT_OUT
            }
            transactionInformation={transactionInformation}
            setTransactionInformation={setTransactionInformation}
            setDoesPartnerNeedUpdateForTransactionInformation={
              setDoesPartnerNeedUpdateForTransactionInformation
            }
            displayEvent={displayEvent}
            setDisplayEvent={setDisplayEvent}
            displayEventHandler={displayEventHandler}
          />

          <Products
            styles={styles}
            allProducts={bperxProducts}
            transactionInformation={transactionInformation}
            setTransactionInformation={setTransactionInformation}
            setDoesPartnerNeedUpdateForTransactionInformation={
              setDoesPartnerNeedUpdateForTransactionInformation
            }
            areProductsVisible={areProductsVisible}
            setProductsVisible={setProductsVisible}
            displayEvent={displayEvent}
            setDisplayEvent={setDisplayEvent}
            displayEventHandler={displayEventHandler}
          />
        </div>
      )}
    </div>
  )
}

export default DefaultTemplate
